import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { siteInductionsService } from '../../services/siteInductionsService';
import { siteService } from '../../services/siteService';
import { companyService } from '../../services/companyService';
import InductionUploadPanel from './components/InductionUploadPanel';
import InductionAssignPanel from './components/InductionAssignPanel';
import CompletionTrackingTable from './components/CompletionTrackingTable';
import InductionDeleteConfirmModal from './components/InductionDeleteConfirmModal';
import Button from '../../components/ui/Button';
import { toast } from 'sonner';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';
import { isSiteReadOnlyStatus } from '../../hooks/useSiteReadOnly';
import { supabase } from '../../lib/supabase';

const SiteInductionsManagement = () => {
  const { user, userProfile } = useAuth();
  // NSherjFh PR 9: Delete + Restore + the View Deleted toggle are
  // admin-only. RLS would permit site_managers via inductions_admin_
  // site_manager_all FOR ALL, but the UI exposes those controls to
  // admins only — same documented pattern as PRs 4-8.
  const isAdmin = userProfile?.role === 'admin';
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const { t } = useLanguage();
  const [sites, setSites] = useState([]);
  const [company, setCompany] = useState(null);
  const [inductions, setInductions] = useState([]);
  const [allAssignments, setAllAssignments] = useState([]);
  const [siteWorkers, setSiteWorkers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSite, setFilterSite] = useState('');

  // NSherjFh PR 9: tab between active and soft-deleted inductions.
  const [viewDeleted, setViewDeleted] = useState(false);
  const [deletingInduction, setDeletingInduction] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, [viewDeleted]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [companyResult, sitesResult] = await Promise.all([
        companyService?.getUserCompany(),
        siteService?.getSitesByUser(),
      ]);

      const comp = companyResult?.data;
      const siteList = sitesResult?.data || [];
      setCompany(comp);
      setSites(siteList);

      if (comp?.id) {
        await loadInductionsData(comp?.id);
      }
    } catch (err) {
      setErrorMsg('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadInductionsData = async (companyId) => {
    // NSherjFh PR 9: deleted view skips the assignments fetch — the
    // tracking grid is hidden in that mode, and getAllAssignments
    // ForCompany already filters out soft-deleted parents (correctly),
    // so it'd come back empty regardless. Cheaper to skip.
    const [inductionsResult, assignmentsResult] = await Promise.all([
      siteInductionsService?.getInductionsForCompany(companyId, { includeDeleted: viewDeleted }),
      viewDeleted
        ? Promise.resolve({ data: [], error: null })
        : siteInductionsService?.getAllAssignmentsForCompany(companyId),
    ]);
    // includeDeleted: true returns BOTH active and deleted rows;
    // narrow to soft-deleted ones for display in the Deleted tab.
    const rows = inductionsResult?.data || [];
    setInductions(viewDeleted ? rows?.filter((i) => i?.deleted_at) : rows);
    setAllAssignments(assignmentsResult?.data || []);
  };

  // NSherjFh PR 9 — soft-delete handlers. Mirrors PRs 3-8 flow.
  const handleDeleteClick = (induction) => {
    setDeletingInduction(induction);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingInduction?.id) {
      setIsDeleteModalOpen(false);
      return;
    }
    const target = deletingInduction;
    try {
      const result = await siteInductionsService?.softDeleteInduction(target?.id);
      if (result?.error) throw new Error(result?.error?.message || 'Delete failed');
      setInductions((prev) => prev?.filter((i) => i?.id !== target?.id));
      toast?.success(`"${target?.title}" moved to Deleted`);
      // Refresh assignments so stats reflect the change.
      if (company?.id) await loadInductionsData(company?.id);
    } catch (err) {
      console.error('[SiteInductionsManagement] delete failed:', err);
      toast?.error(err?.message || 'Failed to delete induction');
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingInduction(null);
    }
  };

  const handleRestore = async (induction) => {
    try {
      const result = await siteInductionsService?.restoreInduction(induction?.id);
      if (result?.error) throw new Error(result?.error?.message || 'Restore failed');
      setInductions((prev) => prev?.filter((i) => i?.id !== induction?.id));
      toast?.success(`"${induction?.title}" restored`);
    } catch (err) {
      console.error('[SiteInductionsManagement] restore failed:', err);
      toast?.error(err?.message || 'Failed to restore induction');
    }
  };

  const handleSiteChangeForWorkers = async (siteId) => {
    if (!siteId) { setSiteWorkers([]); return; }
    setErrorMsg('');
    const result = await siteInductionsService?.getWorkersForSite(siteId);
    if (result?.error) {
      console.error('[SiteInductionsManagement] getWorkersForSite failed:', { code: result?.error?.code, message: result?.error?.message, err: result?.error });
      setErrorMsg(result?.error?.message || 'Failed to load workers for this site');
    }
    // Legitimate empty result (no workers assigned) sets [] with no error —
    // empty-state UI renders, no banner. Banner only fires on actual error.
    setSiteWorkers(result?.data || []);
  };

  const handleUpload = async ({ title, siteId, file }) => {
    if (subscriptionReadOnly) return;
    if (!company?.id || !user?.id) return;
    if (siteId) {
      const { data: pickedSite } = await supabase
        ?.from('sites')
        ?.select('status')
        ?.eq('id', siteId)
        ?.maybeSingle();
      if (isSiteReadOnlyStatus(pickedSite?.status)) {
        setErrorMsg('This site is completed — please reactivate to continue.');
        return;
      }
    }
    setUploading(true);
    setErrorMsg('');
    const result = await siteInductionsService?.uploadInduction(title, siteId, file, user?.id, company?.id);
    setUploading(false);
    if (result?.error) {
      setErrorMsg(result?.error?.message || 'Upload failed');
      return;
    }
    setSuccessMsg('Induction document uploaded successfully');
    setTimeout(() => setSuccessMsg(''), 4000);
    await loadInductionsData(company?.id);
    await handleSiteChangeForWorkers(siteId);
  };

  const handleAssign = async (inductionId, workerIds) => {
    if (subscriptionReadOnly) return;
    setAssigning(true);
    setErrorMsg('');
    const result = await siteInductionsService?.assignInductionToWorkers(inductionId, workerIds);
    setAssigning(false);
    if (result?.error) {
      setErrorMsg(result?.error?.message || 'Assignment failed');
      return;
    }
    setSuccessMsg(`Induction assigned to ${workerIds?.length} worker${workerIds?.length !== 1 ? 's' : ''}`);
    setTimeout(() => setSuccessMsg(''), 4000);
    if (company?.id) await loadInductionsData(company?.id);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)] pt-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Site Inductions Management - SiteProof</title></Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 py-6">

            <SubscriptionReadOnlyBanner className="mb-6" />

            {/* Page Header */}
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Icon name="GraduationCap" size={20} className="text-accent" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">
                    {viewDeleted ? 'Deleted inductions' : t('inductionsTitle')}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {viewDeleted
                      ? 'Inductions removed from active views. Restore to bring them back.'
                      : t('inductionsSubtitle')}
                  </p>
                </div>
              </div>
              {/* NSherjFh PR 9: admin-only View Deleted toggle. */}
              {isAdmin && (
                <Button
                  variant={viewDeleted ? 'default' : 'outline'}
                  onClick={() => { setErrorMsg(''); setSuccessMsg(''); setViewDeleted((v) => !v); }}
                  iconName={viewDeleted ? 'FolderOpen' : 'Trash2'}
                  iconPosition="left"
                >
                  {viewDeleted ? 'View active' : 'View deleted'}
                </Button>
              )}
            </div>

            {/* Alerts */}
            {successMsg && (
              <div className="flex items-center gap-2 bg-success/10 border border-success/20 text-success rounded-lg px-4 py-3 mb-4 text-sm">
                <Icon name="CheckCircle" size={16} />
                <span>{successMsg}</span>
              </div>
            )}
            {errorMsg && (
              <div className="flex items-center gap-2 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg px-4 py-3 mb-4 text-sm">
                <Icon name="AlertCircle" size={16} />
                <span>{errorMsg}</span>
              </div>
            )}

            {/* NSherjFh PR 9: active view = full management workspace;
                deleted view = just a restorable list. Hide stats /
                upload / assign / tracking grid when viewing the
                recycle bin. */}
            {!viewDeleted && (
              <>
                {/* Stats Row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: t('inductionsTotalInductions'), value: inductions?.length, icon: 'FileText', color: 'text-accent bg-accent/10' },
                    { label: t('inductionsAssigned'), value: allAssignments?.length, icon: 'Users', color: 'text-blue-600 bg-blue-50' },
                    { label: t('inductionsCompleted'), value: allAssignments?.filter((a) => a?.completed)?.length, icon: 'CheckCircle', color: 'text-success bg-success/10' },
                    { label: t('inductionsPending'), value: allAssignments?.filter((a) => !a?.completed)?.length, icon: 'Clock', color: 'text-destructive bg-destructive/10' },
                  ]?.map((stat) => (
                    <div key={stat?.label} className="bg-card border border-border rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${stat?.color}`}>
                          <Icon name={stat?.icon} size={18} />
                        </div>
                        <div>
                          <p className="text-xl font-bold text-foreground">{stat?.value}</p>
                          <p className="text-xs text-muted-foreground">{stat?.label}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Upload + Assign Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <InductionUploadPanel
                    sites={sites}
                    onUpload={handleUpload}
                    uploading={uploading}
                    onSiteChange={handleSiteChangeForWorkers}
                    readOnly={subscriptionReadOnly}
                  />
                  <InductionAssignPanel
                    inductions={inductions}
                    workers={siteWorkers}
                    onAssign={handleAssign}
                    onInductionChange={handleSiteChangeForWorkers}
                    assigning={assigning}
                    readOnly={subscriptionReadOnly}
                  />
                </div>

                {/* Inductions list with admin Delete buttons. New per-doc
                    list (mirroring PR 3 rams-management). */}
                {inductions?.length > 0 && (
                  <div className="mb-6 bg-card border border-border rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="FileText" size={16} className="text-accent" />
                      Inductions
                    </h3>
                    <div className="space-y-2">
                      {inductions?.map((induction) => (
                        <div key={induction?.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{induction?.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {induction?.site?.site_name || ''}
                            </p>
                          </div>
                          {isAdmin && (
                            <button
                              onClick={() => handleDeleteClick(induction)}
                              className="flex items-center gap-1.5 text-xs font-medium text-destructive hover:text-destructive/80 px-3 py-1.5 rounded-lg border border-destructive/30 hover:bg-destructive/5 transition-colors"
                              aria-label={`Delete ${induction?.title}`}
                            >
                              <Icon name="Trash2" size={13} />
                              Delete
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Completion Tracking Table */}
                <CompletionTrackingTable
                  rows={allAssignments}
                  loading={false}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  filterStatus={filterStatus}
                  onFilterStatusChange={setFilterStatus}
                  filterSite={filterSite}
                  onFilterSiteChange={setFilterSite}
                  sites={sites}
                />
              </>
            )}

            {/* Deleted view: simple restorable list. Storage file
                preserved; restore re-attaches everything to active
                surfaces. */}
            {viewDeleted && (
              <div className="bg-card border border-border rounded-xl p-4">
                {inductions?.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No deleted inductions.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {inductions?.map((induction) => (
                      <div key={induction?.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{induction?.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {induction?.site?.site_name || 'Site removed'}
                            {induction?.deleted_at ? ` · deleted ${new Date(induction?.deleted_at)?.toLocaleDateString('en-GB')}` : ''}
                          </p>
                        </div>
                        {isAdmin && (
                          <button
                            onClick={() => handleRestore(induction)}
                            className="flex items-center gap-1.5 text-xs font-medium text-accent hover:text-accent/80 px-3 py-1.5 rounded-lg border border-accent/30 hover:bg-accent/5 transition-colors"
                            aria-label={`Restore ${induction?.title}`}
                          >
                            <Icon name="RotateCcw" size={13} />
                            Restore
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
        <InductionDeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => { setIsDeleteModalOpen(false); setDeletingInduction(null); }}
          onConfirm={handleConfirmDelete}
          inductionTitle={deletingInduction?.title || ''}
        />
      </div>
    </>
  );
};

export default SiteInductionsManagement;
